/*
 * Agency mode's request shapes and answers (draft 13): tasks, projects, agreements and retainers, the clients table,
 * client results and monthly reports, the agency Home, and the sales call slides.
 */
import { z } from "zod";
import { HEALTHS, LIFECYCLES, TASK_KINDS, type Slide } from "./agency.js";

const text = (max: number) => z.string().trim().max(max);
const optional = (max: number) => text(max).nullable().optional();
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use a date like 2026-11-03.");
const clock = z.string().regex(/^\d{2}:\d{2}$/, "Use a time like 14:30.");
const cents = z.number().int().min(0).max(10_000_000);

// Tasks ----------------------------------------------------------------------------------------------------------------

export const taskInput = z
  .object({
    title: text(200).min(1, "Say what needs doing."),
    note: optional(2000),
    kind: z.enum(TASK_KINDS).default("todo"),
    dueOn: day,
    dueTime: clock.nullable().optional(),
    assignedTo: z.uuid().nullable().optional(),
    clientId: z.uuid().nullable().optional(),
    leadId: z.uuid().nullable().optional(),
    projectId: z.uuid().nullable().optional(),
  })
  .strict();
export type TaskInput = z.infer<typeof taskInput>;

export const taskPatch = taskInput.partial().extend({ done: z.boolean().optional() }).strict();
export type TaskPatch = z.infer<typeof taskPatch>;

export const TASK_VIEWS = ["mine", "open", "today", "overdue", "done"] as const;
export const taskListQuery = z.object({
  view: z.enum(TASK_VIEWS).default("open"),
  clientId: z.uuid().optional(),
  projectId: z.uuid().optional(),
  leadId: z.uuid().optional(),
});

export const taskRow = z.object({
  id: z.uuid(),
  title: z.string(),
  note: z.string().nullable(),
  kind: z.enum(TASK_KINDS),
  dueOn: z.string(),
  dueTime: z.string().nullable(),
  /** "Nov 3, 2:00 PM CT": the time with its zone, as the owner wants on every agency time. */
  dueWords: z.string(),
  overdue: z.boolean(),
  assignedTo: z.uuid().nullable(),
  assignedName: z.string().nullable(),
  clientId: z.uuid().nullable(),
  clientName: z.string().nullable(),
  leadId: z.uuid().nullable(),
  leadName: z.string().nullable(),
  projectId: z.uuid().nullable(),
  projectName: z.string().nullable(),
  /** onboarding for the checklist made with a new plan. */
  group: z.string().nullable(),
  doneAt: z.string().nullable(),
});
export type TaskRow = z.infer<typeof taskRow>;

// Projects -------------------------------------------------------------------------------------------------------------

export const PROJECT_STATUSES = ["onboarding", "active", "paused", "done", "stopped"] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];
export const PROJECT_STATUS_WORDS: Record<ProjectStatus, string> = {
  onboarding: "Getting set up",
  active: "Running",
  paused: "Paused",
  done: "Done",
  stopped: "Stopped",
};
export const PROJECT_KINDS = ["retainer", "project"] as const;

export const projectInput = z
  .object({
    clientId: z.uuid(),
    name: text(160).min(1, "Name the project."),
    kind: z.enum(PROJECT_KINDS).default("project"),
    status: z.enum(PROJECT_STATUSES).default("active"),
    ownerId: z.uuid().nullable().optional(),
    startOn: day.nullable().optional(),
    dueOn: day.nullable().optional(),
    notes: optional(4000),
  })
  .strict();
export const projectPatch = projectInput.omit({ clientId: true }).partial().strict();

export const projectRow = z.object({
  id: z.uuid(),
  number: z.number(),
  clientId: z.uuid(),
  clientName: z.string(),
  name: z.string(),
  kind: z.enum(PROJECT_KINDS),
  status: z.enum(PROJECT_STATUSES),
  ownerId: z.uuid().nullable(),
  ownerName: z.string().nullable(),
  startOn: z.string().nullable(),
  dueOn: z.string().nullable(),
  notes: z.string().nullable(),
  monthlyCents: z.number().nullable(),
  agreementId: z.uuid().nullable(),
  openTasks: z.number(),
  doneTasks: z.number(),
});
export type ProjectRow = z.infer<typeof projectRow>;

// Agreements -----------------------------------------------------------------------------------------------------------

export const agreementRow = z.object({
  id: z.uuid(),
  clientId: z.uuid(),
  clientName: z.string(),
  quoteId: z.uuid(),
  quoteNumber: z.number(),
  projectId: z.uuid().nullable(),
  monthlyCents: z.number(),
  setupCents: z.number(),
  startOn: z.string(),
  termEndsOn: z.string(),
  noticeOn: z.string().nullable(),
  endsOn: z.string().nullable(),
  state: z.enum(["active", "notice", "ended"]),
  /** Signed the auto pay box: the written OK to charge the saved card for this plan's monthly bills. */
  autoPay: z.boolean(),
  signedBy: z.string(),
  signedAt: z.string(),
  billedMonths: z.number(),
  nextBillOn: z.string().nullable(),
  leftIfStoppedCents: z.number(),
  firstInvoiceId: z.uuid().nullable(),
});
export type AgreementRow = z.infer<typeof agreementRow>;

export const noticeBody = z.object({ noticeOn: day }).strict();

// Clients --------------------------------------------------------------------------------------------------------------

export const agencyClientRow = z.object({
  id: z.uuid(),
  /** The business. */
  name: z.string(),
  contact: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  ownerId: z.uuid().nullable(),
  ownerName: z.string().nullable(),
  retainerCents: z.number(),
  lifecycle: z.enum(LIFECYCLES),
  lastActivityAt: z.string().nullable(),
  openTasks: z.number(),
  health: z.enum(HEALTHS),
  linkedCompanyId: z.uuid().nullable(),
});
export type AgencyClientRow = z.infer<typeof agencyClientRow>;

export const clientOwnerBody = z.object({ ownerId: z.uuid().nullable() }).strict();
/** Show one of our own companies' numbers as this client's results (a test client, owner 2026-10-22). */
export const clientLinkBody = z.object({ companyId: z.uuid().nullable() }).strict();

export const agencyClientDetail = z.object({
  client: agencyClientRow,
  agreements: z.array(agreementRow),
  projects: z.array(projectRow),
  tasks: z.array(taskRow),
  deals: z.array(z.object({ id: z.uuid(), name: z.string(), stage: z.string(), valueCents: z.number().nullable() })),
  /** Companies this login may link as the client's results (our own locations). */
  linkable: z.array(z.object({ id: z.uuid(), name: z.string() })),
});
export type AgencyClientDetail = z.infer<typeof agencyClientDetail>;

// Results and reports --------------------------------------------------------------------------------------------------

export const monthNumbers = z.object({
  leads: z.number().int().min(0).max(100_000).nullable(),
  spendCents: cents.nullable(),
  reviews: z.number().int().min(0).max(100_000).nullable(),
  rating: z.number().min(0).max(5).nullable(),
  rankAverage: z.number().min(1).max(21).nullable(),
  collectedCents: cents.nullable(),
});
export type MonthNumbers = z.infer<typeof monthNumbers>;

export const clientResults = z.object({
  clientId: z.uuid(),
  name: z.string(),
  /** linked: read live from one of our own companies; typed: from the monthly numbers the team typed; none. */
  source: z.enum(["linked", "typed", "none"]),
  sourceName: z.string().nullable(),
  /** Linked to a company this login may not see: no numbers. */
  noAccess: z.boolean(),
  health: z.enum(HEALTHS),
  leadsNow: z.number().nullable(),
  leadsBefore: z.number().nullable(),
  spendCents: z.number().nullable(),
  costPerLeadCents: z.number().nullable(),
  reviews: z.number().nullable(),
  rating: z.number().nullable(),
  rankNow: z.number().nullable(),
  rankBefore: z.number().nullable(),
  collectedCents: z.number().nullable(),
  retainerCents: z.number(),
  nextTask: z.string().nullable(),
  /** Six months, oldest first, for the small lines. */
  months: z.array(z.object({ month: z.string(), leads: z.number().nullable(), rank: z.number().nullable() })),
});
export type ClientResults = z.infer<typeof clientResults>;

export const reportBody = monthNumbers.partial().extend({
  month: z.string().regex(/^\d{4}-\d{2}$/, "Use a month like 2026-10."),
  note: optional(4000),
});

export const reportRow = z.object({
  id: z.uuid(),
  clientId: z.uuid(),
  month: z.string(),
  numbers: monthNumbers,
  /** typed by the team, or taken from a linked company when the report was made. */
  source: z.enum(["typed", "linked"]),
  health: z.enum(HEALTHS),
  note: z.string().nullable(),
  createdAt: z.string(),
});
export type ReportRow = z.infer<typeof reportRow>;

// Home -----------------------------------------------------------------------------------------------------------------

export const agencyHome = z.object({
  timezone: z.string(),
  /** Monthly recurring revenue: every live plan's monthly amount. */
  mrrCents: z.number(),
  livePlans: z.number(),
  onNotice: z.number(),
  stages: z.array(z.object({ stageId: z.uuid(), name: z.string(), count: z.number(), valueCents: z.number() })),
  tasksDue: z.array(taskRow),
  overdue: z.number(),
  unpaid: z.object({ count: z.number(), cents: z.number() }),
  rankMoves: z.array(
    z.object({ clientId: z.uuid(), name: z.string(), rankNow: z.number(), rankBefore: z.number(), health: z.enum(HEALTHS) }),
  ),
});
export type AgencyHome = z.infer<typeof agencyHome>;

// Deals ----------------------------------------------------------------------------------------------------------------

export const slidesBody = z
  .object({
    /** Look up nearby businesses on Google. It costs money, so it is asked first (owner rule). */
    lookUpCompetitors: z.boolean().default(false),
  })
  .strict();

export const deckRow = z.object({
  id: z.uuid(),
  leadId: z.uuid(),
  slides: z.array(z.object({ title: z.string(), lines: z.array(z.string()) })),
  paidChecks: z.boolean(),
  createdAt: z.string(),
});
export type DeckRow = Omit<z.infer<typeof deckRow>, "slides"> & { slides: Slide[] };

export const proposalFromDealBody = z
  .object({
    description: text(200).min(1).default("Monthly marketing"),
    monthlyCents: cents.min(100, "Type the monthly amount."),
    setupCents: cents.default(0),
    /** Extra monthly lines, like Google profile management from the local package (answer 116). */
    extras: z
      .array(z.object({ description: text(200).min(1), monthlyCents: cents.min(100, "Type the monthly amount.") }).strict())
      .max(10)
      .default([]),
  })
  .strict();
